/**
 * Multi-tenant branding hook
 * Fetches org branding (logo, name) based on hostname
 */

import { useState, useEffect, createContext, useContext } from 'react';

const BrandingContext = createContext(null);

const DEFAULT_BRANDING = { name: 'Jubly', logoUrl: null, logoKind: null, monogram: null, domain: null, slug: null, isApp: false };

// jubly.net / jubly.me / jubly.org (and subdomains) and localhost keep the Jubly look.
export function isJublyBrandHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h || h === 'localhost' || h === '127.0.0.1') return true;
  return ['jubly.net', 'jubly.me', 'jubly.org'].some((b) => h === b || h.endsWith('.' + b));
}

// On an app host, swap every favicon link for the app's own logo (never the hummingbird).
function applyAppFavicon(url) {
  if (!url) return;
  document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]').forEach((el) => el.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = url;
  document.head.appendChild(link);
}

export function BrandingProvider({ children }) {
  // On an app host start in a neutral pending state, so the Jubly name and logo never
  // flash before /api/branding answers.
  const [branding, setBranding] = useState(() =>
    (typeof window !== 'undefined' && !isJublyBrandHost(window.location.hostname))
      ? { ...DEFAULT_BRANDING, name: '', isApp: true, pending: true }
      : DEFAULT_BRANDING);

  useEffect(() => {
    const hostname = window.location.hostname;
    if (isJublyBrandHost(hostname)) return; // default Jubly branding

    fetch('/api/branding')
      .then(r => r.json())
      .then(d => {
        if (d.branding) {
          setBranding({ ...DEFAULT_BRANDING, ...d.branding, isApp: true });
          applyAppFavicon(d.branding.logoUrl);
        } else {
          setBranding(DEFAULT_BRANDING); // unknown host (e.g. the Railway domain)
        }
      })
      .catch(() => setBranding(DEFAULT_BRANDING));
  }, []);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext) || DEFAULT_BRANDING;
}
